import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import { ComicPanel, ComicButton, ComicHeading } from '../components/ComicPanel';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Check } from 'lucide-react';

const Signup = () => {
const [email, setEmail] = useState('');
const [password, setPassword] = useState('');
const [confirmPassword, setConfirmPassword] = useState('');
const [showPassword, setShowPassword] = useState(false);
const [loading, setLoading] = useState(false);
const { signup } = useAuth();
const navigate = useNavigate();

const passwordChecks = {
length: password.length >= 8,
uppercase: /[A-Z]/.test(password),
number: /[0-9]/.test(password),
};

const isPasswordValid = passwordChecks.length && passwordChecks.uppercase && passwordChecks.number;
const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

const handleSubmit = async (e) => {
e.preventDefault();

if (!isPasswordValid) {
  toast.error('Password does not meet requirements');
  return;
}

if (!passwordsMatch) {
  toast.error('Passwords do not match');
  return;
}

setLoading(true);

try {
  await signup(email, password);
  toast.success('Account created! Check your email to verify.');
  navigate('/login');
} catch (error) {
  const message = error.response?.data?.detail || 'Signup failed';
  toast.error(message);
} finally {
  setLoading(false);
}

};

return ( <Layout hideFooter> <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-900"> <ComicPanel className="w-full max-w-md p-8"> <ComicHeading as="h1" className="text-3xl text-center mb-8">
Join the Club </ComicHeading>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* EMAIL */}
        <div>
          <Label className="text-cyan-400 uppercase font-bold tracking-widest text-sm">
            Email
          </Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-2 bg-slate-900 border-2 border-slate-700 text-white focus:border-cyan-400 rounded-none"
            placeholder="you@example.com"
          />
        </div>

        {/* PASSWORD */}
        <div>
          <Label className="text-cyan-400 uppercase font-bold tracking-widest text-sm">
            Password
          </Label>

          <div className="relative mt-2">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-slate-900 border-2 border-slate-700 text-white focus:border-cyan-400 rounded-none pr-10"
              placeholder="••••••••"
            />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {/* Password requirements */}
          <div className="mt-3 space-y-1">
            <PasswordCheck check={passwordChecks.length} text="At least 8 characters" />
            <PasswordCheck check={passwordChecks.uppercase} text="One uppercase letter" />
            <PasswordCheck check={passwordChecks.number} text="One number" />
          </div>
        </div>

        {/* CONFIRM PASSWORD */}
        <div>
          <Label className="text-cyan-400 uppercase font-bold tracking-widest text-sm">
            Confirm Password
          </Label>

          <Input
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="mt-2 bg-slate-900 border-2 border-slate-700 text-white focus:border-cyan-400 rounded-none"
            placeholder="••••••••"
          />

          {confirmPassword && (
            <p className={`mt-2 text-sm ${passwordsMatch ? 'text-green-500' : 'text-red-500'}`}>
              {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
            </p>
          )}
        </div>

        {/* SUBMIT */}
        <ComicButton 
          type="submit" 
          variant="secondary" 
          className="w-full"
          disabled={loading || !isPasswordValid || !passwordsMatch}
        >
          {loading ? 'Creating account...' : 'Create Account'}
        </ComicButton>

      </form>

      {/* LOGIN LINK */}
      <div className="mt-8 text-center">
        <p className="text-slate-400">
          Already have an account?{' '}
          <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-bold">
            Sign In
          </Link>
        </p>
      </div>

      {/* TERMS */}
      <p className="mt-6 text-xs text-slate-500 text-center">
        By signing up, you agree to our{' '}
        <Link to="/terms" className="text-slate-400 hover:text-white">Terms</Link>
        {' '}and{' '}
        <Link to="/privacy" className="text-slate-400 hover:text-white">Privacy Policy</Link>.
      </p>

    </ComicPanel>
  </div>
</Layout>
);
};

const PasswordCheck = ({ check, text }) => (

  <div className="flex items-center gap-2 text-sm">
    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${check ? 'bg-green-500' : 'bg-slate-700'}`}>
      {check && <Check className="w-3 h-3 text-white" />}
    </div>
    <span className={check ? 'text-green-500' : 'text-slate-500'}>{text}</span>
  </div>
);

export default Signup;
